import { connect } from 'react-redux';
import React, { useEffect, useState } from 'react';

import i18next from 'i18next';

import { Box, Grid, IconButton, Typography } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';

import { DATAGRID_LOCALES } from '@commons/utils/dataGrid';
import { ExportButton } from '@commons/ExportButton';
import { showErrorMessage } from '@actions/messageconfirmation';

import { channelColumns, deliveryColumns } from './utils/column';
import { DELIVERY_PLATFORMS, METRIC_TYPE_OPTIONS } from './utils/config';
import { fetchChannelData, transformToSummaryData, transformToTableData } from './utils/fetch';
import { generateExport, generateExportDelivery } from './utils/export';
import { getClientInfo } from '@selectors/client';
import { METRIC_TYPE } from '../common/utils';
import ChannelComparisonChart from './ChannelComparisonChart';
import ChannelPieChart from './ChannelPieChart';
import DeliveryComparisonChart from './DeliveryComparisonChart';
import MetricCard from '../common/MetricCard';

const VIEW_MODES = {
  TABLE: 'table',
  CHART: 'chart',
};

const ChannelPanel = ({
  selectedStores,
  currentPeriod,
  previousPeriod,
  showErrorMessage,
  selectedMetric,
  isPercentage,
  client: { defaultChannelId, clientChannels },
}) => {
  // States
  const [viewMode, setViewMode] = useState(VIEW_MODES.TABLE);
  const [deliveryViewMode, setDeliveryViewMode] = useState(VIEW_MODES.TABLE);
  const [isLoading, setIsLoading] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [channelData, setChannelData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [selectedStoresForChart, setSelectedStoresForChart] = useState([]);
  const [selectedStoresForDeliveryChart, setSelectedStoresForDeliveryChart] = useState([]);
  // Load channel data

  const loadChannelData = async () => {
    if (!selectedStores || selectedStores.length === 0) {
      return;
    }

    setIsLoading(true);
    try {
      const storeIds = selectedStores.map((store) => store.id);
      const [currentData, previousData] = await Promise.all([
        fetchChannelData(
          storeIds,
          currentPeriod.startDate,
          currentPeriod.endDate,
          defaultChannelId,
          clientChannels,
        ),
        fetchChannelData(
          storeIds,
          previousPeriod.startDate,
          previousPeriod.endDate,
          defaultChannelId,
          clientChannels,
        ),
      ]);
      setChannelData({
        current: currentData,
        previous: previousData,
      });
    } catch (error) {
      showErrorMessage(i18next.t('GENERAL.GLOBAL_FETCH_ERROR'));
    } finally {
      setIsLoading(false);
    }
  };

  // Load data on mount and when filters change
  useEffect(() => {
    if (
      selectedStores.length &&
      currentPeriod.startDate &&
      currentPeriod.endDate &&
      previousPeriod.startDate &&
      previousPeriod.endDate
    ) {
      loadChannelData();
    }
  }, [
    selectedStores,
    currentPeriod.startDate,
    currentPeriod.endDate,
    previousPeriod.startDate,
    previousPeriod.endDate,
  ]);

  useEffect(() => {
    // Transform data for summary KPIs
    const summary = transformToSummaryData(channelData, selectedMetric.id, isPercentage);
    setSummaryData(summary);

    // Transform data for table
    const table = transformToTableData(
      channelData,
      selectedStores,
      selectedMetric.id,
      isPercentage,
    );
    setTableData(table);
  }, [selectedMetric.id, isPercentage, channelData]);

  // Handle table row/cell click to show pie chart for specific store
  const handleGridClick = (params) => {
    const store = selectedStores.find((s) => s.id === params.row.id);
    if (store) {
      // Update pie chart data for selected store
      setSelectedStoresForChart([store]);
      setViewMode(VIEW_MODES.CHART);
    }
  };

  const handleDeliveryGridClick = (params) => {
    const store = selectedStores.find((s) => s.id === params.row.id);
    if (store) {
      // Update pie chart data for selected store
      setSelectedStoresForDeliveryChart([store]);
      setDeliveryViewMode(VIEW_MODES.CHART);
    }
  };

  // Export handler
  const handleExport = () => {
    try {
      generateExport(tableData, currentPeriod, previousPeriod, selectedMetric);
    } catch (error) {
      showErrorMessage(i18next.t('GENERAL.EXPORT_FAILURE'));
    }
  };

  const handleExportDelivery = () => {
    try {
      generateExportDelivery(tableData, currentPeriod, previousPeriod, selectedMetric);
    } catch (error) {
      showErrorMessage(i18next.t('GENERAL.EXPORT_FAILURE'));
    }
  };

  return (
    <Box sx={{ mb: 5, paddingBottom: '64px' }}>
      {/* Metric Cards */}
      <Grid spacing={3} sx={{ mb: 5 }} container>
        <Grid lg={3} md={6} sm={6} xs={12} item>
          <MetricCard
            currentValue={summaryData?.total?.current}
            isCurrency={selectedMetric.type === METRIC_TYPE.CURRENCY}
            isLoading={isLoading}
            previousValue={summaryData?.total?.previous}
            title={selectedMetric.name}
          />
        </Grid>
        <Grid lg={3} md={6} sm={6} xs={12} item>
          <MetricCard
            currentValue={summaryData?.eatin?.current}
            isCurrency={selectedMetric.type === METRIC_TYPE.CURRENCY && !isPercentage}
            isLoading={isLoading}
            isPercentage={isPercentage}
            previousValue={summaryData?.eatin?.previous}
            title={i18next.t('ANALYSIS.PERFORMANCE.DINE_IN')}
          />
        </Grid>
        <Grid lg={3} md={6} sm={6} xs={12} item>
          <MetricCard
            currentValue={summaryData?.takeaway?.current}
            isCurrency={selectedMetric.type === METRIC_TYPE.CURRENCY && !isPercentage}
            isLoading={isLoading}
            isPercentage={isPercentage}
            previousValue={summaryData?.takeaway?.previous}
            title={i18next.t('ANALYSIS.PERFORMANCE.TAKEAWAY')}
          />
        </Grid>
        <Grid lg={3} md={6} sm={6} xs={12} item>
          <MetricCard
            currentValue={summaryData?.delivery?.current}
            isCurrency={selectedMetric.type === METRIC_TYPE.CURRENCY && !isPercentage}
            isLoading={isLoading}
            isPercentage={isPercentage}
            previousValue={summaryData?.delivery?.previous}
            title={i18next.t('ANALYSIS.PERFORMANCE.DELIVERY')}
          />
        </Grid>
      </Grid>

      {/* Table/Chart Toggle */}
      <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">{i18next.t('ANALYSIS.PERFORMANCE.CHANNEL_DETAIL')}</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box>
            <IconButton
              sx={{
                width: 42,
                height: 42,
                backgroundColor: viewMode === 'table' ? 'white' : 'transparent',
                border: viewMode === 'table' ? '1px solid #E0E0E0' : '1px solid transparent',
              }}
              onClick={() => setViewMode(VIEW_MODES.TABLE)}
            >
              <img
                alt="Table"
                src="/images/inpulse/list-black-small.svg"
                style={{ width: 20, height: 20 }}
              />
            </IconButton>
            <IconButton
              sx={{
                width: 42,
                height: 42,
                backgroundColor: viewMode === 'chart' ? 'white' : 'transparent',
                border: viewMode === 'chart' ? '1px solid #E0E0E0' : '1px solid transparent',
              }}
              onClick={() => setViewMode(VIEW_MODES.CHART)}
            >
              <img alt="Chart" src="/images/inpulse/margin.svg" style={{ width: 20, height: 20 }} />
            </IconButton>
          </Box>
          <ExportButton
            handleClick={handleExport}
            hasLabel={false}
            isDisabled={isLoading || !tableData.length}
          />
        </Box>
      </Box>

      {/* Table or Pie Chart */}
      {viewMode === VIEW_MODES.TABLE ? (
        <Box
          sx={{
            height: 600,
            maxHeight: 600,
            overflow: 'auto',
            cursor: 'pointer',
          }}
        >
          <DataGridPro
            columns={channelColumns(selectedMetric, isPercentage)}
            initialState={{
              sorting: {
                sortModel: [{ field: 'storeName', sort: 'asc' }],
              },
            }}
            loading={isLoading}
            localeText={
              DATAGRID_LOCALES[i18next.language]?.components?.MuiDataGrid?.defaultProps?.localeText
            }
            pageSize={20}
            rows={tableData}
            sx={{
              '& .MuiCircularProgress-root': {
                color: 'black',
              },
            }}
            disableColumnFilter
            disableColumnMenu
            disableColumnPinning
            disableColumnReorder
            disableColumnSelector
            disableDensitySelector
            onRowClick={handleGridClick}
          />
        </Box>
      ) : (
        <ChannelPieChart
          categories={['eatin', 'takeaway', 'delivery']}
          initialSelectedStores={selectedStoresForChart}
          isLoading={isLoading}
          isPercentage={isPercentage}
          metricOptions={METRIC_TYPE_OPTIONS()}
          metricType={selectedMetric.id}
          selectedMetric={selectedMetric}
          selectedStores={selectedStores}
          tableData={tableData}
          title={i18next.t('ANALYSIS.PERFORMANCE.CHANNEL_BREAKDOWN')}
        />
      )}

      {/* Store Comparison Chart */}
      <Box sx={{ mt: 4 }}>
        <ChannelComparisonChart
          isCurrency={selectedMetric.type === METRIC_TYPE.CURRENCY}
          isLoading={isLoading}
          isPercentage={isPercentage}
          metricType={selectedMetric.id}
          selectedMetric={selectedMetric}
          selectedStores={selectedStores}
          tableData={tableData}
        />
      </Box>

      {/* Delivery Comparison Chart */}
      <Box sx={{ mt: 4 }}>
        <DeliveryComparisonChart
          isLoading={isLoading}
          isPercentage={isPercentage}
          selectedMetric={selectedMetric}
          selectedStores={selectedStores}
          tableData={tableData}
        />
      </Box>

      {/* Table/Chart Toggle */}
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          mb: 3,
          mt: 4,
        }}
      >
        <Typography variant="h5">{i18next.t('ANALYSIS.PERFORMANCE.DELIVERY_DETAIL')}</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box>
            <IconButton
              sx={{
                width: 42,
                height: 42,
                backgroundColor: deliveryViewMode === 'table' ? 'white' : 'transparent',
                border:
                  deliveryViewMode === 'table' ? '1px solid #E0E0E0' : '1px solid transparent',
              }}
              onClick={() => setDeliveryViewMode(VIEW_MODES.TABLE)}
            >
              <img
                alt="Table"
                src="/images/inpulse/list-black-small.svg"
                style={{ width: 20, height: 20 }}
              />
            </IconButton>
            <IconButton
              sx={{
                width: 42,
                height: 42,
                backgroundColor: deliveryViewMode === 'chart' ? 'white' : 'transparent',
                border:
                  deliveryViewMode === 'chart' ? '1px solid #E0E0E0' : '1px solid transparent',
              }}
              onClick={() => setDeliveryViewMode(VIEW_MODES.CHART)}
            >
              <img alt="Chart" src="/images/inpulse/margin.svg" style={{ width: 20, height: 20 }} />
            </IconButton>
          </Box>
          <ExportButton
            handleClick={handleExportDelivery}
            hasLabel={false}
            isDisabled={isLoading || !tableData.length}
          />
        </Box>
      </Box>

      {/* Table or Pie Chart */}
      {deliveryViewMode === VIEW_MODES.TABLE ? (
        <Box
          sx={{
            height: 600,
            maxHeight: 600,
            overflow: 'auto',
            cursor: 'pointer',
          }}
        >
          <DataGridPro
            columns={deliveryColumns(selectedMetric, isPercentage)}
            initialState={{
              sorting: {
                sortModel: [{ field: 'storeName', sort: 'asc' }],
              },
            }}
            loading={isLoading}
            localeText={
              DATAGRID_LOCALES[i18next.language]?.components?.MuiDataGrid?.defaultProps?.localeText
            }
            pageSize={20}
            rows={tableData}
            sx={{
              '& .MuiCircularProgress-root': {
                color: 'black',
              },
            }}
            disableColumnFilter
            disableColumnMenu
            disableColumnPinning
            disableColumnReorder
            disableColumnSelector
            disableDensitySelector
            onRowClick={handleDeliveryGridClick}
          />
        </Box>
      ) : (
        <ChannelPieChart
          categories={Object.values(DELIVERY_PLATFORMS)}
          data={tableData}
          initialSelectedStores={selectedStoresForDeliveryChart}
          isLoading={isLoading}
          isPercentage={isPercentage}
          metricOptions={METRIC_TYPE_OPTIONS()}
          metricType={selectedMetric.id}
          selectedMetric={selectedMetric}
          selectedStores={selectedStores}
          tableData={tableData}
          title={i18next.t('ANALYSIS.PERFORMANCE.PLATFORM_BREAKDOWN')}
        />
      )}
    </Box>
  );
};

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
});

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
});

export default connect(mapStateToProps, mapDispatchToProps)(ChannelPanel);
